import { ChangeDetectionStrategy, Component, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import MarkdownIt from 'markdown-it';
import { ChatbotWebsocketConversationHelperService } from '../chatbot/services/chatbot-websocket-conversation.helper.service';
import { ChatbotDownloadService } from '../chatbot/services/chatbot-download.service';
import { ToastService } from '../../../../../projects/client/src/app/shared/services';

@Component({
  selector: 'hf-markdown',
  templateUrl: './markdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarkdownComponent implements OnChanges {
  @Input() data = '';
  @Input() isStreaming = false;
  renderedHtml = '';

  private readonly DOWNLOAD_PATH_MARKER = '/api/v1/download/gcs/';
  private readonly FILE_ICON_SRC = '/assets/icons/chatbot/download-icon.svg';
  // Reuse one MarkdownIt instance
  private md!: MarkdownIt;

  constructor(
    private readonly chatbotWebSocketConversationHelperService: ChatbotWebsocketConversationHelperService,
    private readonly chatbotDownloadService: ChatbotDownloadService,
    private readonly toastService: ToastService
  ) {
    this.md = this.createMarkdown();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.renderMarkdown(changes.data.currentValue ?? '');
    }
  }

  @HostListener('click', ['$event'])
  async onClick(e: MouseEvent) {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const tile = target.closest('a.download-file-container') as HTMLAnchorElement | null;
    if (!tile) return;

    e.preventDefault();
    e.stopPropagation();

    const url = tile.getAttribute('data-download-url') || tile.href;
    const filename = tile.getAttribute('data-filename') || this.displayNameFromUrl(url);

    try {
      await this.chatbotDownloadService.download(url, filename);
    } catch (err) {
      console.error(err);
      this.toastService.add({ header: 'Download failed.', message: 'Please try again.' }, 'error');
    }
  }

  private renderMarkdown(src: string) {
    let s = this.chatbotWebSocketConversationHelperService.characterFixes(src);

    const hasUnicodeBullets = /[\u2022\u2043\u00B7]/.test(s);
    if (hasUnicodeBullets) s = this.normalizeBulletsOutsideCode(s);

    // While streaming: hide the actual download link(s) and show placeholder tile
    if (this.isStreaming && this.mightContainDownloadLink(s)) {
      const cleaned = this.truncateFromDownloadMarker(s);
      const htmlUI = this.md.render(cleaned || '');
      this.renderedHtml = this.injectPreparingDownloadTile(htmlUI);
      return;
    }

    const html = this.md.render(s || '');
    this.renderedHtml = this.upgradeDownloadAnchorsToTiles(html);
  }

  private injectPreparingDownloadTile(renderedHtml: string): string {
    const doc = this.parseHtml(renderedHtml);

    // Don’t duplicate
    if (doc.querySelector('.download-file-container--preparing')) {
      return this.finalizeRenderedHtml(doc);
    }

    const tile = this.buildDownloadTile(doc, {
      kind: 'preparing',
      filename: 'Preparing download…'
    });

    doc.body.appendChild(tile);
    return this.finalizeRenderedHtml(doc);
  }

  private upgradeDownloadAnchorsToTiles(renderedHtml: string): string {
    const doc = this.parseHtml(renderedHtml);
    const anchors = Array.from(doc.querySelectorAll('a[href]')) as HTMLAnchorElement[];

    for (const a of anchors) {
      const href = a.getAttribute('href') || '';
      if (!href || !href.includes(this.DOWNLOAD_PATH_MARKER)) continue;

      const fullUrl = this.rebuildFullDownloadUrl(a, href);
      const normalizedUrl = this.normalizeForUi(fullUrl);
      const filename = this.displayNameFromUrl(normalizedUrl);

      const tile = this.buildDownloadTile(doc, {
        kind: 'ready',
        url: normalizedUrl,
        filename
      });

      // Replace surrounding <p> for cleaner layout
      const p = a.closest('p');
      if (p) p.replaceWith(tile);
      else a.replaceWith(tile);
    }

    return this.finalizeRenderedHtml(doc);
  }

  private buildDownloadTile(
    doc: Document,
    opts: { kind: 'preparing'; filename: string } | { kind: 'ready'; url: string; filename: string }
  ): HTMLElement {
    const isPreparing = opts.kind === 'preparing';

    const el = doc.createElement(isPreparing ? 'div' : 'a');
    el.className = isPreparing
      ? 'download-file-container download-file-container--preparing'
      : 'download-file-container';

    if (!isPreparing) {
      const a = el as HTMLAnchorElement;
      const ready = opts as { kind: 'ready'; url: string; filename: string };
      a.href = ready.url;
      a.setAttribute('data-download-url', ready.url);
      a.setAttribute('data-filename', ready.filename);
    }

    const img = doc.createElement('img');
    img.src = this.FILE_ICON_SRC;
    img.alt = 'download file icon';

    const details = doc.createElement('span');
    details.className = 'download-file-container_details';

    const nameSpan = doc.createElement('span');
    nameSpan.className = 'download-file-container_details_filename';
    nameSpan.textContent = opts.filename;

    details.appendChild(nameSpan);
    el.appendChild(img);
    el.appendChild(details);

    return el;
  }

  private mightContainDownloadLink(s: string): boolean {
    return !!s && s.includes(this.DOWNLOAD_PATH_MARKER);
  }

  private truncateFromDownloadMarker(src: string): string {
    if (!src) return src;

    const idx = src.indexOf(this.DOWNLOAD_PATH_MARKER);
    if (idx < 0) return src;

    // Cut from the start of the line containing the marker (so we remove the whole markdown link line)
    const lineStart = src.lastIndexOf('\n', idx);
    const cutAt = lineStart >= 0 ? lineStart : 0;

    return src
      .substring(0, cutAt)
      .replace(/\n{3,}/g, '\n\n')
      .trimEnd();
  }

  private normalizeForUi(rawUrl: string): string {
    let url = (rawUrl || '').trim();
    for (let i = 0; i < 3; i++) {
      const decoded = this.tryDecodeURIComponent(url);
      if (decoded === url) break;
      url = decoded;
    }
    return encodeURI(url);
  }

  private rebuildFullDownloadUrl(a: HTMLAnchorElement, href: string): string {
    const p = a.closest('p');
    const text = (p?.textContent || '').trim();

    // If href already ends with an extension, keep it
    if (/\.(csv|xlsx|pdf)\b/i.test(href)) return href;

    const start = text.indexOf(this.DOWNLOAD_PATH_MARKER);
    if (start >= 0) {
      const tail = text.substring(start);
      const extMatch = tail.match(/\.(csv|xlsx|pdf)\b/i);
      if (extMatch) {
        const end = tail.indexOf(extMatch[0]) + extMatch[0].length;
        const path = tail.substring(0, end);

        try {
          const base = new URL(href);
          return new URL(path, base.origin).toString();
        } catch {
          return path;
        }
      }
    }

    // Fallback: append whatever follows the visible href, if it ends in a file ext
    const after = text.split(href)[1];
    if (after && /\.(csv|xlsx|pdf)\b/i.test(after)) {
      return (href + after).trim();
    }

    return href;
  }

  private displayNameFromUrl(href: string): string {
    const noQuery = href.split(/[?#]/)[0];
    const last = noQuery.substring(noQuery.lastIndexOf('/') + 1);

    let s = last;
    for (let i = 0; i < 3; i++) {
      const next = this.tryDecodeURIComponent(s);
      if (next === s) break;
      s = next;
    }
    return s || 'download';
  }

  private tryDecodeURIComponent(s: string): string {
    try {
      return decodeURIComponent(s);
    } catch {
      return s;
    }
  }

  // HTML parse helpers
  private parseHtml(html: string): Document {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  private finalizeRenderedHtml(doc: Document): string {
    return doc.body.innerHTML;
  }

  // MarkdownIt helpers and bullet normalization
  private createMarkdown(): MarkdownIt {
    const md = new MarkdownIt({
      html: false,
      linkify: true,
      typographer: true
    });

    // Disable markdown image syntax (![alt](url)). Even with html:false, markdown-it
    // would otherwise emit a real <img src> for an https URL, which Angular's
    // sanitizer permits — allowing injected external image requests / tracking pixels.
    md.disable('image');

    md.validateLink = (url: string) => {
      try {
        const u = new URL(url, 'http://_');
        const scheme = u.protocol.replace(':', '').toLowerCase();
        return ['http', 'https', 'mailto', 'tel'].includes(scheme);
      } catch {
        return false;
      }
    };

    const defaultRender =
      md.renderer.rules.link_open || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));

    md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
      const aIndex = tokens[idx].attrIndex('target');
      if (aIndex < 0) tokens[idx].attrPush(['target', '_blank']);
      else tokens[idx].attrs![aIndex][1] = '_blank';

      const relIndex = tokens[idx].attrIndex('rel');
      const relValue = 'noopener noreferrer';
      if (relIndex < 0) tokens[idx].attrPush(['rel', relValue]);
      else tokens[idx].attrs![relIndex][1] = relValue;

      return defaultRender(tokens, idx, options, env, self);
    };

    return md;
  }

  private normalizeBulletsOutsideCode(src: string): string {
    const parts = src.split(/(```[\s\S]*?```)/g);
    return parts.map((part, i) => (i % 2 === 1 ? part : this.normalizeBullets(part))).join('');
  }

  private normalizeBullets(src: string): string {
    const BULLETS = '[\\u2022\\u2043\\u00B7]';
    let s = src;

    s = s.replace(new RegExp(`([^\\n\\r])\\s*(${BULLETS})(?=\\S)`, 'g'), `$1\n- `);
    s = s.replace(new RegExp(`(^|[\\n\\r])[ \\t]*${BULLETS}[ \\t]*`, 'g'), `$1- `);
    s = s.replace(/([^\n\r])\n(- )/g, `$1\n\n$2`);

    return s;
  }
}
